import React, { FC, useState, useMemo, useEffect } from 'react';
import { Autocomplete, Box, Chip, Grid, Paper, TextField, Tooltip } from '@mui/material';
import { DataGrid, GridCellParams, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useGetAllPriceListsQuery, useGetAllProductCategoriesQuery, useGetAllProductsQuery, useGetSuburbStatusQuery } from '@services';
import { AddToCart, LoadingSpinner } from '@components';
import { OrderProductInfoModal } from './OrderProductInfoModal';
import { generateUniqueKey, toCurrency } from '@helpers';
import { Product, ProductCategory } from '@models';
import SearchIcon from '@mui/icons-material/Search';
import debounce from 'lodash.debounce';
import { useAppDispatch, useAppSelector } from '@hooks/redux-custom';
import DynamicFeedIcon from '@mui/icons-material/DynamicFeed';
import { CustomField } from '@models';
import { getZoneId } from '@helpers';
import { setStateBasedPriceList } from '@store/slices/products';
import { AU_STATES } from '@constants';

type ProductsTableList = {
  id: number;
  sku: string;
  name: string;
  price?: number;
  categories: string[];
  custom_fields?: CustomField[];
}[];

export const OrderProductList: FC = () => {
  const dispatch = useAppDispatch();
  const { data: priceLists } = useGetAllPriceListsQuery();

  dispatch(setStateBasedPriceList(priceLists));
  const shippingAddress = useAppSelector((state) => state.order.value.shippingAddress);

  const { data: products, isFetching } = useGetAllProductsQuery();
  const { data: categories } = useGetAllProductCategoriesQuery();
  const { data: suburbProductsStatus } = useGetSuburbStatusQuery({
    suburb: `${shippingAddress?.postal_code}_${shippingAddress?.city.toLowerCase().replace(/\s/g, '')}`,
  });

  const zoneId = getZoneId(categories);

  const stateBasedPriceList = useAppSelector((state) => state?.products?.value?.statePriceList);

  const [selectedStateIndex, setSelectedStateIndex] = useState(0);

  //Calling function and setting state price list category
  useEffect(() => {
    if (shippingAddress && stateBasedPriceList) {
      const state = AU_STATES.find((state) => state.value === shippingAddress?.state_or_province);
      const index = stateBasedPriceList.findIndex(
        (listItem: { name: string }) => listItem.name === `${state?.id.toLowerCase()}_price_list`,
      );

      setSelectedStateIndex(index);
    }
  }, [shippingAddress, stateBasedPriceList]);

  const uniqueCategories = useMemo(() => {
    // Subset of categories - filters our Menu1, Zones and subcategories of Zones.
    const filteredCategories = categories?.filter((c) => {
      return c.parent_id !== zoneId && c.id !== zoneId && c.id !== 40;
    });

    if (filteredCategories) {
      const categorySet = new Set<string>();

      filteredCategories.forEach((category) => categorySet.add(category.name));

      return categorySet;
    }
  }, [categories, zoneId]);

  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<number>();

  const showItemInfo = (cell: GridCellParams) => {
    if (cell.field === 'add-to-cart') return;

    setSelectedProductId(cell.row.id);
    setIsModalOpen(true);
  };

  const getCategories = (product: Product) => {
    return product.categories.reduce((productCategories, id) => {
      const category = categories?.find((c) => c.id === id && c.parent_id !== zoneId);
      return category ? [...productCategories, category] : productCategories;
    }, [] as ProductCategory[]);
  };

  const getCategoryNameById = (categoryId: number) => {
    const category = categories?.find((c) => {
      return c.id === categoryId;
    });

    if (category) {
      return category.name;
    } else {
      return 'No Category';
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 2,
      renderCell: (it) => {
        const { value, row } = it;
        const isBundleIndex = row.custom_fields.findIndex((field: { name: string; id: number }) => field.name === 'is_bundle');
        const isBundle = row?.custom_fields[isBundleIndex]?.value;
        return (
          <Box display={'flex'} justifyContent={'center'}>
            {isBundleIndex > -1 && isBundle === 'true' && <DynamicFeedIcon sx={{ marginRight: 1 }} />}
            <Tooltip placement="top" title={value}>
              <span>{value}</span>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: 'categories',
      headerName: 'Categories',
      headerAlign: 'center',
      flex: 1.4,
      renderCell: ({ value }) =>
        value?.map((category: string) => <Chip key={generateUniqueKey()} label={category} size="small" sx={{ mr: 1 }} />),
    },
    { field: 'price', headerName: 'Price', headerAlign: 'center', flex: 0.5, valueFormatter: ({ value }) => toCurrency(Number(value)) },
    {
      field: 'add-to-cart',
      flex: 0.45,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <div style={{ lineHeight: '1.2', textAlign: 'center' }}>
          Add to
          <div style={{ paddingTop: '0.2em' }}>Cart</div>
        </div>
      ),
      renderCell: (params: GridRenderCellParams) => {
        const productSku = params.row.sku;

        const disabled =
          suburbProductsStatus?.oos.includes(productSku) ||
          suburbProductsStatus?.snoozed.includes(productSku) ||
          suburbProductsStatus?.deranged.includes(productSku);

        return <AddToCart disabled={disabled} selectedStateIndex={selectedStateIndex} productId={params.row.id} />;
      },
    },
  ];

  const [searchText, setSearchText] = useState('');

  const rows = products?.reduce((products, product) => {
    const categories = getCategories(product).map(({ id }) => getCategoryNameById(id));

    if (!selectedCategories.length || selectedCategories.find((value) => categories.includes(value))) {
      //Sorting price based on state and base pricing
      const getStateBasedPrice = () =>
        stateBasedPriceList
          ? stateBasedPriceList[selectedStateIndex].records.find((item) => item.product_id === product.id)?.price
          : undefined;

      products.push({
        id: product.id,
        sku: product.sku,
        name: product.name,
        price: getStateBasedPrice() ?? product.price,
        categories,
        custom_fields: product.custom_fields,
      });
    }

    return products;
  }, [] as ProductsTableList);

  const handleSearchTextChangeDebounced = useMemo(() => debounce((text: string) => setSearchText(text), 300), []);

  return isFetching ? (
    <LoadingSpinner isOpen={isFetching} />
  ) : (
    <Box paddingLeft={1} paddingTop={2} data-testid="order-product-list">
      <Grid container justifyContent="flex-start" sx={{ mb: 1 }} spacing={2}>
        <Grid item xs={6}>
          <Autocomplete
            multiple
            options={(uniqueCategories && Array.from(uniqueCategories)) || []}
            getOptionLabel={(option: string) => option}
            filterSelectedOptions
            onChange={(event, categories) => {
              setSelectedCategories(categories);
            }}
            renderInput={(params) => <TextField {...params} label="Filter by categories" placeholder="Categories" />}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            variant="outlined"
            placeholder="Search for product..."
            onChange={(e) => handleSearchTextChangeDebounced(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1 }} />,
            }}
          />
        </Grid>
      </Grid>
      <Paper sx={{ height: '90vh' }}>
        {rows && (
          <DataGrid
            sortingOrder={['desc', 'asc']}
            rows={rows}
            columns={columns}
            data-testid="order-product-table"
            disableSelectionOnClick
            onCellClick={(cell) => showItemInfo(cell)}
            disableColumnSelector
            disableColumnFilter
            filterModel={{
              items: [
                {
                  columnField: 'name',
                  operatorValue: 'contains',
                  value: searchText,
                },
              ],
            }}
            rowBuffer={10}
            getRowClassName={(params) => {
              let className = '';
              const sku = params.row.sku;

              if (suburbProductsStatus?.oos.includes(sku) || suburbProductsStatus?.snoozed.includes(sku)) {
                className = 'oos';
              }

              if (suburbProductsStatus?.deranged.includes(sku)) {
                className = 'oos deranged';
              }

              return className;
            }}
            sx={{
              '& .MuiDataGrid-row.oos': {
                opacity: 0.5,
              },
              '& .MuiDataGrid-row.oos:hover': {
                backgroundColor: 'unset',
              },
              '& .MuiDataGrid-row.deranged': {
                position: 'relative',
              },
              '& .MuiDataGrid-row.deranged:hover': {
                backgroundColor: 'unset',
              },
              '& .MuiDataGrid-row.deranged:before': {
                content: '" "',
                position: 'absolute',
                top: '50%',
                left: '0.5%',
                borderBottom: '1px solid #9e9c9c',
                width: '99%',
              },
            }}
          />
        )}
        {selectedProductId && (
          <OrderProductInfoModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            productId={selectedProductId}
            suburbProductsStatus={suburbProductsStatus}
            selectedStateIndex={selectedStateIndex}
          />
        )}
      </Paper>
    </Box>
  );
};
